import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router';
import ReactGA from 'react-ga4';
import { alertError } from 'shared/lib/functions/alert';
import { useAppDispatch, useAppSelector } from 'shared/lib/hooks/redux';
import { getFileDataAsBase64String } from 'shared/lib/functions/getFileDataAsBase64String';
import useRecaptcha from 'shared/lib/hooks/useRecaptcha';
import verifyCaptcha from 'shared/lib/functions/verifyCaptcha';
import { ENV_DEVELOPMENT } from 'shared/consts/common';
import { addFile } from 'entities/Upload/store/upload/reducers/ActionCreator';
import { PATHS } from 'shared/consts/paths';
import { SubTitle, Uploader, BasicLoader } from 'shared/ui';
import { IPreparedFile } from 'shared/ui/Uploader/Uploader.types';
import { Container, Inner, Description, RelatedLinks, TrustStrip } from './UploadSection.style';

const UploadSection = () => {
    const [filesUploaded, setFilesUploaded] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [preparedFiles, setPreparedFiles] = useState<IPreparedFile[]>([]);
    const { token } = useAppSelector(state => state.uploadReducer);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getToken } = useRecaptcha();

    const handleFilesUploadedAll = (files: File[]) => {
        setFilesUploaded(files);
    };

    const handleFilesAdd = useCallback(
        async (filesToAdd: File[]) => {
            // TODO: Re-enable captcha once RECAPTCHA_SECRET_KEY is verified in Cloudflare
            // if (process.env.REACT_APP_ENV !== ENV_DEVELOPMENT) {
            //     try {
            //         const captchaToken = await getToken('csv_upload');
            //         const passed = await verifyCaptcha(captchaToken);
            //
            //         if (!passed) {
            //             alertError('', 'Verification failed. Please try again.');
            //
            //             return;
            //         }
            //     } catch {
            //         alertError('', 'Verification failed. Please try again.');
            //
            //         return;
            //     }
            // }

            const currentFiles: IPreparedFile[] = [];

            for await (const file of filesToAdd) {
                if (file.type !== 'text/csv') {
                    alertError('', 'Please use the file type: csv');

                    return;
                }

                if (file.size === 0) {
                    alertError('', 'File is Empty');
                } else {
                    currentFiles.push({
                        name: file.name,
                        file: file,
                        encodedData: await getFileDataAsBase64String(file),
                    });
                }
            }

            setPreparedFiles(currentFiles);
        },
        [getToken],
    );

    const handleLoad = useCallback(() => {
        if (token) {
            let count = 0;

            preparedFiles.forEach(file => {
                setIsLoading(true);
                const formData = new FormData();

                formData.append('file', file.file);

                const addFileStatus = dispatch(
                    addFile({
                        formData,
                        token,
                    }),
                );

                addFileStatus
                    .then(state => {
                        count = count + 1;

                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        if (state && state.error) {
                            setIsLoading(false);
                        } else if (count === preparedFiles.length) {
                            ReactGA.event({
                                category: 'PhoneCheckr',
                                action: 'File Uploaded',
                            });

                            navigate(PATHS.UPLOAD);
                        }
                    })
                    .catch(() => {
                        setIsLoading(false);
                    });
            });
        }
    }, [preparedFiles]);

    useEffect(() => {
        if (filesUploaded.length) {
            handleFilesAdd(filesUploaded);
        }
    }, [filesUploaded, handleFilesAdd]);

    useEffect(() => {
        if (preparedFiles.length) {
            handleLoad();
        }
    }, [preparedFiles, handleLoad]);

    return (
        <Container id="upload">
            <Inner>
                <Description>
                    <h1
                        style={{
                            fontWeight: 700,
                            fontSize: '42px',
                            lineHeight: 1.2,
                            textAlign: 'center',
                            color: '#121217',
                            margin: '0 0 12px',
                        }}
                    >
                        Free Phone Number Checker
                    </h1>
                    <SubTitle text="Cleaning a phone list before a campaign? Upload a CSV and get line type, carrier, and validity for every number in under 60 seconds. No code, no account, no subscription." />
                </Description>
                <Uploader isShowInformation onFilesUploadedAll={handleFilesUploadedAll} />
                <TrustStrip>
                    No account required · Results in seconds · Trusted by sales, marketing & ops teams
                </TrustStrip>
                <RelatedLinks>
                    Also: <Link to={PATHS.VOIP_CHECKER}>Free VOIP Number Checker</Link>
                    {' · '}
                    <Link to={PATHS.CARRIER_LOOKUP}>Phone Carrier Lookup</Link>
                    {' · '}
                    <Link to={PATHS.BULK_PHONE_VALIDATION}>Bulk Phone Validation</Link>
                    {' · '}
                    <Link to={PATHS.HOW_TO_VERIFY_PHONE}>How to Verify a Phone Number</Link>
                </RelatedLinks>
            </Inner>
            {isLoading && <BasicLoader isCenter isFullScreen />}
        </Container>
    );
};

export default UploadSection;
